import React, { useState } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../components/Auth/AuthContext';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Alert, AlertDescription } from '../../components/ui/alert';
import { Eye, EyeOff, Mail, Lock, AlertCircle, Loader2, Shield, ArrowLeft, Send, CheckCircle } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function LoginPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const { login, verifyMfaCode, mfaRequired, mfaMethod, cancelMfa } = useAuth();
  
  const [formData, setFormData] = useState({
    email: '',
    password: '',
  });
  const [mfaCode, setMfaCode] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [emailNotVerified, setEmailNotVerified] = useState(false);
  const [resendingEmail, setResendingEmail] = useState(false);
  const [emailResent, setEmailResent] = useState(false);

  const from = location.state?.from?.pathname || '/';

  const handleLogin = async (e) => {
    e.preventDefault();
    setError('');
    setEmailNotVerified(false);
    setEmailResent(false);
    setLoading(true);

    try {
      const result = await login(formData.email, formData.password);
      
      if (result.requiresMfa) {
        // MFA required, show MFA form
        setLoading(false);
        return;
      }

      // Login successful
      navigate(from, { replace: true });
    } catch (err) {
      // Check if error is about email verification
      if (err.message.includes('vérifier votre email') || err.message.includes('verify your email')) {
        setEmailNotVerified(true);
      }
      setError(err.message);
      setLoading(false);
    }
  };

  const handleResendVerification = async () => {
    setResendingEmail(true);
    setError('');
    
    try {
      const response = await fetch(`${API_URL}/api/auth/resend-verification`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ email: formData.email }),
      });
      
      const data = await response.json();
      
      if (!response.ok) {
        throw new Error(data.detail || 'Erreur lors de l\'envoi');
      }
      
      setEmailResent(true);
      setEmailNotVerified(false);
    } catch (err) {
      setError(err.message);
    } finally {
      setResendingEmail(false);
    }
  };

  const handleMfaVerify = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      await verifyMfaCode(mfaCode);
      navigate(from, { replace: true });
    } catch (err) {
      setError(err.message);
      setLoading(false);
    }
  };

  const handleCancelMfa = () => {
    cancelMfa();
    setMfaCode('');
    setError('');
  };

  // MFA verification form
  if (mfaRequired) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-blue-50 to-indigo-100 p-4">
        <Card className="w-full max-w-md">
          <CardHeader className="text-center">
            <div className="mx-auto w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mb-4">
              <Shield className="h-8 w-8 text-green-600" />
            </div>
            <CardTitle className="text-2xl">Vérification en deux étapes</CardTitle>
            <CardDescription>
              {mfaMethod === 'totp' 
                ? 'Entrez le code de votre application d\'authentification'
                : 'Un code a été envoyé à votre email'
              }
            </CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleMfaVerify} className="space-y-4">
              {error && (
                <Alert variant="destructive">
                  <AlertCircle className="h-4 w-4" />
                  <AlertDescription>{error}</AlertDescription>
                </Alert>
              )}

              <div className="space-y-2">
                <Label htmlFor="mfaCode">Code de vérification</Label>
                <Input
                  id="mfaCode"
                  type="text"
                  placeholder="000000"
                  className="text-center text-2xl tracking-widest"
                  value={mfaCode}
                  onChange={(e) => setMfaCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                  maxLength={6}
                  autoFocus
                  required
                />
              </div>

              <Button type="submit" className="w-full" disabled={loading || mfaCode.length !== 6}>
                {loading ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Vérification...
                  </>
                ) : (
                  'Vérifier'
                )}
              </Button>

              <Button type="button" variant="ghost" className="w-full" onClick={handleCancelMfa}>
                <ArrowLeft className="mr-2 h-4 w-4" />
                Retour à la connexion
              </Button>
            </form>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Handle back navigation
  const handleGoBack = () => {
    // If there's a previous page in history, go back; otherwise go to home
    if (window.history.length > 1 && document.referrer) {
      navigate(-1);
    } else {
      navigate('/');
    }
  };

  // Main login form
  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-blue-50 to-indigo-100 p-4">
      {/* Back button */}
      <Button
        variant="ghost"
        onClick={handleGoBack}
        className="absolute top-4 left-4 text-gray-600 hover:text-gray-900 hover:bg-white/50"
      >
        <ArrowLeft className="h-4 w-4 mr-2" />
        Retour
      </Button>

      <Card className="w-full max-w-md">
        <CardHeader className="text-center">
          <div className="mx-auto w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mb-4 overflow-hidden">
            <img src="/branding/logo-64.png" alt="Sun-Spot" className="w-12 h-12 object-contain" />
          </div>
          <CardTitle className="text-2xl">Connexion</CardTitle>
          <CardDescription>Connectez-vous à votre compte Sun-Spot</CardDescription>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleLogin} className="space-y-4">
            {/* Email resent success message */}
            {emailResent && (
              <Alert className="bg-green-50 border-green-200">
                <CheckCircle className="h-4 w-4 text-green-600" />
                <AlertDescription className="text-green-800">
                  Un nouvel email de vérification a été envoyé à <strong>{formData.email}</strong>. 
                  Vérifiez votre boîte de réception et vos spams.
                </AlertDescription>
              </Alert>
            )}

            {/* Error with resend verification option */}
            {error && (
              <Alert variant={emailNotVerified ? "default" : "destructive"} className={emailNotVerified ? "bg-amber-50 border-amber-200" : ""}>
                <AlertCircle className={`h-4 w-4 ${emailNotVerified ? "text-amber-600" : ""}`} />
                <AlertDescription className={emailNotVerified ? "text-amber-800" : ""}>
                  {error}
                  {emailNotVerified && (
                    <div className="mt-3">
                      <p className="text-sm mb-2">Vous n'avez pas reçu l'email ou vous l'avez perdu ?</p>
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        onClick={handleResendVerification}
                        disabled={resendingEmail || !formData.email}
                        className="bg-white hover:bg-amber-100 border-amber-300"
                      >
                        {resendingEmail ? (
                          <>
                            <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                            Envoi en cours...
                          </>
                        ) : (
                          <>
                            <Send className="h-4 w-4 mr-2" />
                            Renvoyer l'email de vérification
                          </>
                        )}
                      </Button>
                    </div>
                  )}
                </AlertDescription>
              </Alert>
            )}

            {location.state?.message && (
              <Alert>
                <AlertDescription>{location.state.message}</AlertDescription>
              </Alert>
            )}

            <div className="space-y-2">
              <Label htmlFor="email">Email</Label>
              <div className="relative">
                <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                <Input
                  id="email"
                  type="email"
                  placeholder="votre@email.com"
                  className="pl-10"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  required
                />
              </div>
            </div>

            <div className="space-y-2">
              <div className="flex justify-between items-center">
                <Label htmlFor="password">Mot de passe</Label>
                <Link to="/forgot-password" className="text-sm text-blue-600 hover:underline">
                  Mot de passe oublié ?
                </Link>
              </div>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                <Input
                  id="password"
                  type={showPassword ? 'text' : 'password'}
                  placeholder="••••••••"
                  className="pl-10 pr-10"
                  value={formData.password}
                  onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                  required
                />
                <button
                  type="button"
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                  onClick={() => setShowPassword(!showPassword)}
                >
                  {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                </button>
              </div>
            </div>

            <Button type="submit" className="w-full" disabled={loading}>
              {loading ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Connexion...
                </>
              ) : (
                'Se connecter'
              )}
            </Button>

            <div className="relative my-4">
              <div className="absolute inset-0 flex items-center">
                <div className="w-full border-t border-gray-200"></div>
              </div>
              <div className="relative flex justify-center text-sm">
                <span className="bg-white px-2 text-gray-500">ou</span>
              </div>
            </div>

            <p className="text-center text-sm text-gray-500">
              Pas encore de compte ?{' '}
              <Link to="/register" className="text-blue-600 hover:underline">
                Créer un compte
              </Link>
            </p>
          </form>
        </CardContent>
      </Card>
    </div>
  );
}
